(() => {
  if (window.__marchaDirectModalV4) return;

  window.__marchaDirectModalV4 = true;

  const modalSelector =
    '[data-marcha-direct-modal]';

  const gallerySelector =
    '[data-marcha-gallery="true"], .marcha-product-gallery';

  const reducedMotion =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  const states = new WeakMap();
  const accordionStates = new WeakMap();

  let galleryPointer = null;
  let suppressGalleryClickUntil = 0;

  const getModal = (sectionId) => {
    return document.querySelector(
      `${modalSelector}[data-section-id="${CSS.escape(String(sectionId))}"]`
    );
  };

  const getState = (modal) => {
    if (states.has(modal)) {
      return states.get(modal);
    }

    const state = {
      modal,
      sectionId:
        modal.dataset.sectionId || '',
      dialog: modal.querySelector(
        '[data-marcha-modal-dialog]'
      ),
      stage: modal.querySelector(
        '[data-marcha-modal-stage]'
      ),
      closeButton: modal.querySelector(
        '[data-marcha-modal-close]'
      ),
      previousButton: modal.querySelector(
        '[data-marcha-modal-previous]'
      ),
      nextButton: modal.querySelector(
        '[data-marcha-modal-next]'
      ),
      counter: modal.querySelector(
        '[data-marcha-modal-counter]'
      ),
      cursor: modal.querySelector(
        '[data-marcha-modal-zoom-cursor]'
      ),
      slides: [],
      activeIndex: 0,
      animations: [],
      transitionToken: 0,
      pointer: null,
      suppressZoomUntil: 0,
      cursorFrame: 0,
      closeTimer: 0,
      openedBy: null,
      previousFocus: null,
      mounted: false
    };

    states.set(modal, state);

    return state;
  };

  const refreshSlides = (state) => {
    state.slides = Array.from(
      state.modal.querySelectorAll(
        '[data-marcha-modal-slide]'
      )
    );

    const activeIndex =
      state.slides.findIndex((slide) => {
        return slide.classList.contains(
          'is-active'
        );
      });

    state.activeIndex =
      activeIndex >= 0 ? activeIndex : 0;
  };

  const normalizeIndex = (
    state,
    index
  ) => {
    if (!state.slides.length) return 0;

    return (
      (
        index %
        state.slides.length
      ) +
      state.slides.length
    ) % state.slides.length;
  };

  const updateCounter = (state) => {
    if (!state.counter) return;

    state.counter.textContent =
      `${state.activeIndex + 1} / ${state.slides.length}`;
  };

  const cancelAnimations = (state) => {
    state.transitionToken += 1;

    state.animations.forEach(
      (animation) => {
        try {
          animation.cancel();
        } catch (error) {
          // The animation may already be complete.
        }
      }
    );

    state.animations = [];
  };

  const hideCursor = (state) => {
    state.cursor?.classList.remove(
      'is-visible'
    );
  };

  const resetZoom = (state) => {
    state.modal.classList.remove(
      'is-zoomed'
    );

    state.slides.forEach((slide) => {
      slide.classList.remove(
        'is-zoomed'
      );

      slide.style.removeProperty(
        '--marcha-zoom-x'
      );

      slide.style.removeProperty(
        '--marcha-zoom-y'
      );
    });

    state.cursor?.classList.remove(
      'is-visible',
      'is-minus'
    );
  };

  const getActiveSlide = (state) => {
    return (
      state.slides[state.activeIndex] ||
      null
    );
  };

  const getZoomData = (state) => {
    const slide =
      getActiveSlide(state);

    if (
      !slide ||
      slide.dataset.mediaType !== 'image'
    ) {
      return null;
    }

    const frame = slide.querySelector(
      '.marcha-modal__media-frame'
    );

    const image =
      frame?.querySelector('img');

    if (!frame || !image) return null;

    return {
      slide,
      frame,
      image
    };
  };

  const updateZoomOrigin = (
    event,
    slide,
    frame
  ) => {
    const rect =
      frame.getBoundingClientRect();

    if (!rect.width || !rect.height) {
      return;
    }

    const x = Math.max(
      0,
      Math.min(
        100,
        (
          (
            event.clientX -
            rect.left
          ) /
          rect.width
        ) * 100
      )
    );

    const y = Math.max(
      0,
      Math.min(
        100,
        (
          (
            event.clientY -
            rect.top
          ) /
          rect.height
        ) * 100
      )
    );

    slide.style.setProperty(
      '--marcha-zoom-x',
      `${x}%`
    );

    slide.style.setProperty(
      '--marcha-zoom-y',
      `${y}%`
    );
  };

  const playActiveMedia = (slide) => {
    window.pauseAllMedia?.();

    const deferredMedia =
      slide?.querySelector(
        'deferred-media, product-model'
      );

    deferredMedia?.loadContent?.();
  };

  const syncGallery = (
    state,
    mediaId
  ) => {
    const gallery =
      document.getElementById(
        `MediaGallery-${state.sectionId}`
      );

    gallery?.setActiveMedia?.(
      `${state.sectionId}-${mediaId}`,
      false,
      {
        animate: true,
        skipPageScroll: true
      }
    );
  };

  const finishSlideChange = (
    state,
    incoming,
    nextIndex,
    token,
    shouldSyncGallery
  ) => {
    if (
      token != null &&
      token !== state.transitionToken
    ) {
      return;
    }

    state.slides.forEach((slide) => {
      const active =
        slide === incoming;

      slide.classList.toggle(
        'is-active',
        active
      );

      slide.classList.remove(
        'is-transitioning'
      );

      slide.setAttribute(
        'aria-hidden',
        active ? 'false' : 'true'
      );

      slide.style.removeProperty(
        'z-index'
      );

      slide.style.removeProperty(
        'opacity'
      );

      slide.style.removeProperty(
        'transform'
      );

      slide.style.removeProperty(
        'visibility'
      );
    });

    state.activeIndex = nextIndex;

    updateCounter(state);
    playActiveMedia(incoming);

    if (shouldSyncGallery) {
      syncGallery(
        state,
        incoming.dataset.mediaId
      );
    }
  };

  const goToSlide = (
    state,
    requestedIndex,
    options = {}
  ) => {
    refreshSlides(state);

    if (!state.slides.length) return;

    const nextIndex =
      normalizeIndex(
        state,
        requestedIndex
      );

    const incoming =
      state.slides[nextIndex];

    const outgoing =
      state.slides[state.activeIndex];

    if (!incoming) return;

    resetZoom(state);
    cancelAnimations(state);

    const direction =
      Number(options.direction) ||
      (
        nextIndex >= state.activeIndex
          ? 1
          : -1
      );

    const shouldAnimate =
      options.animate !== false &&
      !reducedMotion.matches &&
      outgoing &&
      outgoing !== incoming;

    if (!shouldAnimate) {
      finishSlideChange(
        state,
        incoming,
        nextIndex,
        null,
        options.syncGallery === true
      );

      return;
    }

    state.transitionToken += 1;

    const token =
      state.transitionToken;

    const distance =
      window.innerWidth <= 749
        ? 13
        : 7;

    const incomingX =
      direction > 0
        ? distance
        : -distance;

    const outgoingX =
      direction > 0
        ? -distance
        : distance;

    const duration =
      Number.parseFloat(
        getComputedStyle(
          state.modal
        ).getPropertyValue(
          '--marcha-modal-transition'
        )
      ) || 450;

    incoming.classList.add(
      'is-active',
      'is-transitioning'
    );

    outgoing.classList.add(
      'is-transitioning'
    );

    incoming.setAttribute(
      'aria-hidden',
      'false'
    );

    outgoing.setAttribute(
      'aria-hidden',
      'true'
    );

    incoming.style.zIndex = '3';
    outgoing.style.zIndex = '2';

    const animationOptions = {
      duration,
      easing:
        'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'forwards'
    };

    const outgoingAnimation =
      outgoing.animate(
        [
          {
            opacity: 1,
            transform:
              'translate3d(0, 0, 0) scale(1)'
          },
          {
            opacity: 0,
            transform:
              `translate3d(${outgoingX}%, 0, 0) scale(0.985)`
          }
        ],
        animationOptions
      );

    const incomingAnimation =
      incoming.animate(
        [
          {
            opacity: 0,
            transform:
              `translate3d(${incomingX}%, 0, 0) scale(0.985)`
          },
          {
            opacity: 1,
            transform:
              'translate3d(0, 0, 0) scale(1)'
          }
        ],
        animationOptions
      );

    state.animations = [
      outgoingAnimation,
      incomingAnimation
    ];

    Promise.all([
      outgoingAnimation.finished.catch(
        () => undefined
      ),
      incomingAnimation.finished.catch(
        () => undefined
      )
    ]).then(() => {
      finishSlideChange(
        state,
        incoming,
        nextIndex,
        token,
        options.syncGallery === true
      );
    });
  };

  const lockPage = () => {
    document.documentElement.classList.add(
      'marcha-modal-scroll-lock'
    );

    document.body.classList.add(
      'marcha-modal-scroll-lock'
    );
  };

  const unlockPage = () => {
    document.documentElement.classList.remove(
      'marcha-modal-scroll-lock'
    );

    document.body.classList.remove(
      'marcha-modal-scroll-lock'
    );
  };

  const findSlideIndex = (
    state,
    mediaId
  ) => {
    const normalizedId =
      String(mediaId || '');

    const index =
      state.slides.findIndex((slide) => {
        return (
          String(
            slide.dataset.mediaId || ''
          ) === normalizedId
        );
      });

    return index >= 0 ? index : 0;
  };

  const openModal = (
    sectionId,
    mediaId,
    opener = null
  ) => {
    mountAll(document);

    const modal =
      getModal(sectionId);

    if (!modal) return;

    const state =
      getState(modal);

    window.clearTimeout(
      state.closeTimer
    );

    state.openedBy = opener;
    state.previousFocus =
      document.activeElement;

    refreshSlides(state);
    resetZoom(state);

    modal.hidden = false;

    modal.setAttribute(
      'aria-hidden',
      'false'
    );

    lockPage();

    goToSlide(
      state,
      findSlideIndex(
        state,
        mediaId
      ),
      {
        animate: false,
        syncGallery: false
      }
    );

    requestAnimationFrame(() => {
      modal.classList.add(
        'is-open'
      );

      state.closeButton?.focus({
        preventScroll: true
      });
    });
  };

  const closeModal = (modal) => {
    const state =
      getState(modal);

    if (
      !modal.classList.contains(
        'is-open'
      )
    ) {
      return;
    }

    cancelAnimations(state);
    resetZoom(state);
    window.pauseAllMedia?.();

    modal.classList.remove(
      'is-open'
    );

    modal.setAttribute(
      'aria-hidden',
      'true'
    );

    unlockPage();

    state.closeTimer =
      window.setTimeout(() => {
        modal.hidden = true;
      }, reducedMotion.matches ? 0 : 220);

    const focusTarget =
      state.openedBy ||
      state.previousFocus;

    if (
      focusTarget instanceof HTMLElement
    ) {
      requestAnimationFrame(() => {
        focusTarget.focus({
          preventScroll: true
        });
      });
    }
  };

  const bindModal = (modal) => {
    const state =
      getState(modal);

    refreshSlides(state);
    updateCounter(state);

    if (state.mounted) return;

    state.mounted = true;

    state.closeButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        closeModal(modal);
      }
    );

    state.previousButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        goToSlide(
          state,
          state.activeIndex - 1,
          {
            animate: true,
            direction: -1,
            syncGallery: true
          }
        );
      }
    );

    state.nextButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        goToSlide(
          state,
          state.activeIndex + 1,
          {
            animate: true,
            direction: 1,
            syncGallery: true
          }
        );
      }
    );

    modal.addEventListener(
      'keydown',
      (event) => {
        if (
          !modal.classList.contains(
            'is-open'
          )
        ) {
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          closeModal(modal);
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();

          goToSlide(
            state,
            state.activeIndex - 1,
            {
              animate: true,
              direction: -1,
              syncGallery: true
            }
          );
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();

          goToSlide(
            state,
            state.activeIndex + 1,
            {
              animate: true,
              direction: 1,
              syncGallery: true
            }
          );
        }
      }
    );

    state.stage?.addEventListener(
      'pointerdown',
      (event) => {
        if (
          event.button !== 0 ||
          modal.classList.contains(
            'is-zoomed'
          ) ||
          event.target.closest(
            '.marcha-modal__arrow, .marcha-modal__close, button, a, video, model-viewer'
          )
        ) {
          return;
        }

        state.pointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          time: performance.now()
        };

        state.stage.setPointerCapture?.(
          event.pointerId
        );
      }
    );

    state.stage?.addEventListener(
      'pointerup',
      (event) => {
        const pointer =
          state.pointer;

        state.pointer = null;

        if (
          !pointer ||
          pointer.id !== event.pointerId
        ) {
          return;
        }

        const deltaX =
          event.clientX - pointer.x;

        const deltaY =
          event.clientY - pointer.y;

        const elapsed =
          performance.now() -
          pointer.time;

        if (
          Math.abs(deltaX) >= 44 &&
          Math.abs(deltaX) >
            Math.abs(deltaY) * 1.15 &&
          elapsed < 900
        ) {
          state.suppressZoomUntil =
            performance.now() + 450;

          const direction =
            deltaX < 0 ? 1 : -1;

          goToSlide(
            state,
            state.activeIndex +
              direction,
            {
              animate: true,
              direction,
              syncGallery: true
            }
          );
        }
      }
    );

    state.stage?.addEventListener(
      'pointercancel',
      () => {
        state.pointer = null;
      }
    );

    state.stage?.addEventListener(
      'click',
      (event) => {
        if (
          performance.now() <
            state.suppressZoomUntil ||
          event.target.closest(
            '.marcha-modal__arrow, .marcha-modal__close, button, a, video, model-viewer'
          )
        ) {
          return;
        }

        const zoomData =
          getZoomData(state);

        if (!zoomData) return;

        const {
          slide,
          frame
        } = zoomData;

        const rect =
          frame.getBoundingClientRect();

        const insideFrame =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        if (!insideFrame) return;

        event.preventDefault();
        event.stopPropagation();

        updateZoomOrigin(
          event,
          slide,
          frame
        );

        const zoomingIn =
          !slide.classList.contains(
            'is-zoomed'
          );

        slide.classList.toggle(
          'is-zoomed',
          zoomingIn
        );

        modal.classList.toggle(
          'is-zoomed',
          zoomingIn
        );

        state.cursor?.classList.toggle(
          'is-minus',
          zoomingIn
        );
      }
    );

    state.stage?.addEventListener(
      'pointermove',
      (event) => {
        if (
          event.pointerType === 'touch' ||
          event.target.closest(
            '.marcha-modal__arrow, .marcha-modal__close, button, a, video, model-viewer'
          )
        ) {
          hideCursor(state);
          return;
        }

        const zoomData =
          getZoomData(state);

        if (!zoomData) {
          hideCursor(state);
          return;
        }

        const {
          slide,
          frame
        } = zoomData;

        const rect =
          frame.getBoundingClientRect();

        const insideFrame =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        if (!insideFrame) {
          hideCursor(state);
          return;
        }

        if (
          slide.classList.contains(
            'is-zoomed'
          )
        ) {
          updateZoomOrigin(
            event,
            slide,
            frame
          );
        }

        cancelAnimationFrame(
          state.cursorFrame
        );

        state.cursorFrame =
          requestAnimationFrame(() => {
            modal.style.setProperty(
              '--marcha-cursor-x',
              `${event.clientX}px`
            );

            modal.style.setProperty(
              '--marcha-cursor-y',
              `${event.clientY}px`
            );

            state.cursor?.classList.toggle(
              'is-minus',
              slide.classList.contains(
                'is-zoomed'
              )
            );

            state.cursor?.classList.add(
              'is-visible'
            );
          });
      }
    );

    state.stage?.addEventListener(
      'pointerleave',
      () => {
        hideCursor(state);
      }
    );
  };

  const mountModal = (modal) => {
    if (
      !(modal instanceof HTMLElement)
    ) {
      return;
    }

    const sectionId =
      modal.dataset.sectionId;

    if (!sectionId) return;

    document
      .querySelectorAll(
        `${modalSelector}[data-section-id="${CSS.escape(sectionId)}"]`
      )
      .forEach((existingModal) => {
        if (existingModal !== modal) {
          existingModal.remove();
        }
      });

    if (
      modal.parentElement !==
      document.body
    ) {
      document.body.appendChild(modal);
    }

    bindModal(modal);
  };

  function mountAll(root = document) {
    if (
      root.matches?.(modalSelector)
    ) {
      mountModal(root);
    }

    root
      .querySelectorAll?.(
        modalSelector
      )
      .forEach(mountModal);
  }

  const getGalleryData = (target) => {
    if (
      !(target instanceof Element)
    ) {
      return null;
    }

    const gallery =
      target.closest(
        gallerySelector
      );

    if (!gallery) return null;

    if (
      target.closest(
        '.marcha-gallery-controls, .slider-button'
      )
    ) {
      return null;
    }

    const slide =
      target.closest(
        '.product__media-item[data-media-id]'
      );

    if (
      !slide ||
      !slide.classList.contains(
        'is-active'
      )
    ) {
      return null;
    }

    const opener =
      slide.querySelector(
        '[data-marcha-media-opener][data-media-id]'
      );

    const openButton =
      slide.querySelector(
        '[data-marcha-media-open][data-media-id]'
      );

    if (!opener || !openButton) {
      return null;
    }

    const sectionId =
      openButton.dataset.sectionId ||
      gallery.dataset.sectionId ||
      gallery.id.replace(
        'MediaGallery-',
        ''
      );

    const mediaId =
      openButton.dataset.mediaId;

    if (!sectionId || !mediaId) {
      return null;
    }

    return {
      gallery,
      slide,
      opener,
      openButton,
      sectionId,
      mediaId
    };
  };

  /*
   * Window capture runs before document and element handlers.
   * It therefore cannot be blocked by Dawn's old modal listeners.
   */
  window.addEventListener(
    'pointerdown',
    (event) => {
      if (event.button !== 0) return;

      const data =
        getGalleryData(event.target);

      if (!data) return;

      galleryPointer = {
        id: event.pointerId,
        target: event.target,
        sectionId: data.sectionId,
        mediaId: data.mediaId,
        opener: data.openButton,
        x: event.clientX,
        y: event.clientY,
        time: performance.now()
      };
    },
    true
  );

  window.addEventListener(
    'pointerup',
    (event) => {
      const pointer =
        galleryPointer;

      galleryPointer = null;

      if (
        !pointer ||
        pointer.id !== event.pointerId
      ) {
        return;
      }

      const deltaX =
        event.clientX - pointer.x;

      const deltaY =
        event.clientY - pointer.y;

      const elapsed =
        performance.now() -
        pointer.time;

      const wasDrag =
        Math.abs(deltaX) > 14 ||
        Math.abs(deltaY) > 14 ||
        elapsed > 900;

      if (wasDrag) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      suppressGalleryClickUntil =
        performance.now() + 500;

      openModal(
        pointer.sectionId,
        pointer.mediaId,
        pointer.opener
      );
    },
    true
  );

  /*
   * Keyboard and normal-click fallback.
   */
  window.addEventListener(
    'click',
    (event) => {
      const data =
        getGalleryData(event.target);

      if (!data) return;

      if (
        performance.now() <
        suppressGalleryClickUntil
      ) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      openModal(
        data.sectionId,
        data.mediaId,
        data.openButton
      );
    },
    true
  );

  const animateAccordion = (
    details,
    summary,
    content,
    shouldOpen
  ) => {
    let state =
      accordionStates.get(details);

    if (!state) {
      state = {
        targetOpen: details.open,
        heightAnimation: null,
        contentAnimation: null
      };

      accordionStates.set(
        details,
        state
      );
    }

    const currentHeight =
      details.getBoundingClientRect()
        .height;

    state.targetOpen = shouldOpen;

    state.heightAnimation?.cancel();
    state.contentAnimation?.cancel();

    if (
      shouldOpen &&
      !details.open
    ) {
      details.open = true;
    }

    details.classList.add(
      'is-animating'
    );

    details.style.height =
      `${currentHeight}px`;

    details.style.overflow =
      'hidden';

    void details.offsetHeight;

    const summaryHeight =
      summary.getBoundingClientRect()
        .height;

    const contentHeight =
      content.getBoundingClientRect()
        .height;

    const endHeight =
      shouldOpen
        ? summaryHeight +
          contentHeight
        : summaryHeight;

    const duration =
      reducedMotion.matches
        ? 1
        : 360;

    const easing =
      'cubic-bezier(0.22, 1, 0.36, 1)';

    state.heightAnimation =
      details.animate(
        [
          {
            height:
              `${currentHeight}px`
          },
          {
            height:
              `${endHeight}px`
          }
        ],
        {
          duration,
          easing,
          fill: 'forwards'
        }
      );

    state.contentAnimation =
      content.animate(
        shouldOpen
          ? [
              {
                opacity: 0,
                transform:
                  'translateY(-8px)'
              },
              {
                opacity: 1,
                transform:
                  'translateY(0)'
              }
            ]
          : [
              {
                opacity: 1,
                transform:
                  'translateY(0)'
              },
              {
                opacity: 0,
                transform:
                  'translateY(-8px)'
              }
            ],
        {
          duration:
            Math.max(
              1,
              duration - 40
            ),
          easing,
          fill: 'forwards'
        }
      );

    state.heightAnimation.onfinish =
      () => {
        details.open = shouldOpen;

        details.classList.remove(
          'is-animating'
        );

        details.style.removeProperty(
          'height'
        );

        details.style.removeProperty(
          'overflow'
        );

        content.style.removeProperty(
          'opacity'
        );

        content.style.removeProperty(
          'transform'
        );

        state.heightAnimation = null;
        state.contentAnimation = null;
      };
  };

  document.addEventListener(
    'click',
    (event) => {
      const summary =
        event.target.closest?.(
          '.marcha-delivery-accordion summary, .marcha-description-accordion summary'
        );

      if (!summary) return;

      const details =
        summary.closest('details');

      const content =
        summary.nextElementSibling;

      if (!details || !content) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const state =
        accordionStates.get(details);

      const targetOpen =
        state?.targetOpen ??
        details.open;

      animateAccordion(
        details,
        summary,
        content,
        !targetOpen
      );
    },
    true
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      mountAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const sectionWrapper =
        event.target;

      const sectionId =
        sectionWrapper?.id?.replace(
          'shopify-section-',
          ''
        );

      if (!sectionId) return;

      getModal(sectionId)?.remove();
    }
  );

  const observer =
    new MutationObserver(
      (mutations) => {
        mutations.forEach(
          (mutation) => {
            mutation.addedNodes.forEach(
              (node) => {
                if (
                  node instanceof HTMLElement
                ) {
                  mountAll(node);
                }
              }
            );
          }
        );
      }
    );

  const initialize = () => {
    mountAll(document);

    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );
  };

  window.MarchaProductModal = {
    open: openModal,

    close(sectionId) {
      const modal =
        getModal(sectionId);

      if (modal) {
        closeModal(modal);
      }
    },

    mount: mountAll
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();